import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { Customer, CustomersApi, useCustomer } from "modules/customers";
import { setGlobalFilteredCustomer } from "store/appSlice";
import { findChildrenIds, UUID } from "utils";

export function useGlobalFilterSearch() {
  const [search, setSearch] = useState(INITIAL_SEARCH);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [searchResults, setSearchResults] = useState<SearchResults>([]);
  const [
    persistedSelectedCustomer,
    setPersistedSelectedCustomer,
  ] = useState<PersistedSelectedCustomerType | null>(null);
  const dispatch = useDispatch();
  const { customer, isCustomerLoading, isCustomerFetching } = useCustomer({
    customerId: persistedSelectedCustomer?.id,
    options: {
      skip: !Boolean(persistedSelectedCustomer),
    },
  });

  const {
    currentData: customersData,
    isLoading: isCustomersDataLoading,
    isFetching: isCustomersDataFetching,
  } = useGetCustomersQuery({
    ...GET_CUSTOMERS_PARAMS,
    page,
    ...(search.length ? { search } : {}),
  });

  const isFetching = isCustomersDataFetching;
  const isLoading = isCustomersDataLoading;

  const dataToLoad = customersData?.customers;
  const hasMore = dataToLoad?.length === RESPONSE_DATA_LIMIT;

  useEffect(() => {
    const canBeLoaded = Array.isArray(dataToLoad) && Boolean(dataToLoad.length);

    if (canBeLoaded) {
      setSearchResults((prevResults) => {
        if (
          page === INITIAL_PAGE &&
          search === INITIAL_SEARCH &&
          prevResults.length
        ) {
          return dataToLoad;
        }

        return prevResults.concat(dataToLoad);
      });
    }
  }, [dataToLoad, page, search]);

  const onSearch = (search: string) => {
    setSearchResults([]);
    setSearch(search);
    setPage(INITIAL_PAGE);
  };

  const onSelectCustomer = useCallback(
    ({ id, name, children }: SelectedCustomer) => {
      let allIds: UUID[] = [id];

      if (!children && customer?.id !== id) {
        setPersistedSelectedCustomer({
          id,
          name,
          allIds,
        });

        return;
      }

      if (children || customer?.children) {
        const childrenData = children || customer?.children || [];

        allIds = allIds.concat(findChildrenIds(childrenData));
      }

      setPage(INITIAL_PAGE);
      setSearch(INITIAL_SEARCH);
      dispatch(
        setGlobalFilteredCustomer({
          id,
          name,
          allIds,
        })
      );
    },
    [customer?.children, customer?.id, dispatch]
  );

  useEffect(() => {
    if (customer) {
      const { id, name, children } = customer;

      onSelectCustomer({ id, name, children });
    }
  }, [customer, onSelectCustomer]);

  const handleLoadMore = () => {
    if (hasMore && !isLoading && !isFetching) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const isNoResults =
    !isFetching &&
    !isLoading &&
    !Boolean(dataToLoad?.length) &&
    !Boolean(searchResults.length);

  return {
    searchResults,
    isLoading,
    isCustomerLoading: isCustomerLoading || isCustomerFetching,
    hasMore,
    isNoResults,
    onSearch,
    onSelectCustomer,
    handleLoadMore,
  };
}

interface PersistedSelectedCustomerType extends SelectedCustomer {
  allIds: UUID[];
}

export type SearchResults = SearchResultCustomer[];

export interface SearchResultCustomer {
  id: CustomersApi.GetCustomerHierarchyResponse["id"];
  name: CustomersApi.GetCustomerHierarchyResponse["name"];
  descendantsCount: Customer["descendantsCount"];
}

export interface SelectedCustomer {
  id: CustomersApi.GetCustomerHierarchyResponse["id"];
  name: CustomersApi.GetCustomerHierarchyResponse["name"];
  parentId?: CustomersApi.GetCustomerHierarchyResponse["parentId"];
  children?: CustomersApi.GetCustomerResponse[];
}

const { useGetCustomersQuery } = CustomersApi;

const INITIAL_SEARCH = "";
const INITIAL_PAGE = 1;
const RESPONSE_DATA_LIMIT = 100;
const GET_CUSTOMERS_PARAMS = {
  limit: RESPONSE_DATA_LIMIT,
  order: "asc",
  sortBy: "name",
} as const;
